import React, { useState, useEffect } from "react";
import { Row, Col, Typography, Button, Card, Spin, message } from "antd";
import { FaMapMarkerAlt, FaWhatsapp, FaHome, FaImage } from "react-icons/fa";
import { useParams } from "react-router-dom";
import CommonFooter from "../components/common/CommonFooter";
import CommonHeader from "../components/common/CommonHeader";
import PropertyCard from "../components/common/PropertyCard";
import { firebaseService } from "../lib/services/firebaseService";
import { Property } from "../types/propert";

const { Title, Text, Paragraph } = Typography;

const PropertyDetail = () => {
  const { id } = useParams();
  const [selectedTab, setSelectedTab] = useState("Overview");
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [similarProperties, setSimilarProperties] = useState([]);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        setLoading(true);
        const propertyData = await firebaseService.getPropertyById(id);
        const propertyInstance = new Property(propertyData);
        setProperty(propertyInstance);

        // Fetch similar properties (same property type)
        await fetchSimilarProperties(propertyInstance.propertyType, id);
      } catch (error) {
        console.error("Error fetching property:", error);
        message.error("Property not found");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProperty();
    }
  }, [id]);

  const fetchSimilarProperties = async (propertyType, currentPropertyId) => {
    try {
      firebaseService.getProperties((properties) => {
        const similar = properties
          .filter(
            (p) => p.propertyType === propertyType && p.id !== currentPropertyId
          )
          .slice(0, 3) // Show only 3 similar properties
          .map((p) => new Property(p));
        setSimilarProperties(similar);
      });
    } catch (error) {
      console.error("Error fetching similar properties:", error);
    }
  };

  const handleWhatsAppClick = () => {
    // Temporarily disabled - will be implemented later
    message.info("Contact feature will be available soon");
  };

  const handleGetDirections = () => {
    if (property && property.googleMap) {
      window.open(property.googleMap, "_blank");
    } else if (property) {
      const address = property.getFullAddress();
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        address
      )}`;
      window.open(mapsUrl, "_blank");
    }
  };

  // Get primary image or fallback
  const getPrimaryImage = () => {
    if (!property) return null;
    return property.getPrimaryImage() || "/images/property-placeholder.jpg";
  };

  // Get thumbnail images or empty array
  const getThumbnailImages = () => {
    if (!property || !property.imageUrls || property.imageUrls.length === 0) {
      return [];
    }
    return property.imageUrls.slice(0, 3);
  };

  // Get display title from statusReason (Flutter form field) or use default title
  const getDisplayTitle = () => {
    if (!property) return "";
    return property.statusReason || property.title || "Premium Property";
  };

  // Generate tab content dynamically from property data
  const getTabContent = () => {
    if (!property) return {};

    return {
      Overview: [
        property.description ||
          "Comprehensive details available upon request. Contact our agents for a private viewing and detailed information about this exquisite property.",
        property.otherFeatures ||
          "Premium features and amenities available. Schedule a consultation to discover all the exclusive benefits this property offers.",
      ],
      Specifications: [
        property.bedrooms > 0 && `${property.bedrooms} Luxurious Bedrooms`,
        property.bathrooms > 0 && `${property.bathrooms} Elegant Bathrooms`,
        property.livingRooms > 0 &&
          `${property.livingRooms} Spacious Living Areas`,
        property.kitchens > 0 && `${property.kitchens} Designer Kitchens`,
        property.parkingSpaces > 0 &&
          `${property.parkingSpaces} Secure Parking Spaces`,
        property.propertyStatus &&
          `Property Status: ${property.propertyStatus}`,
        property.furnishing && `Furnishing: ${property.furnishing}`,
        property.getPropertySize() &&
          `Total Area: ${property.getPropertySize()}`,
        property.plotSize > 0 &&
          `Plot Size: ${property.plotSize} ${property.areaUnit || "sq.ft"}`,
        property.builtUpArea > 0 &&
          `Built-up Area: ${property.builtUpArea} ${
            property.areaUnit || "sq.ft"
          }`,
        property.ownershipStatus && `Ownership: ${property.ownershipStatus}`,
        property.mortgageStatus &&
          `Mortgage Status: ${property.mortgageStatus}`,
        property.listedBy && `Listed By: ${property.listedBy}`,
        property.exclusivity && "Exclusive Listing",
      ].filter(Boolean),
      Location: [
        // Updated to match Flutter form structure
        property.city && `City: ${property.city}`,
        property.landmark && `Address: ${property.landmark}`,
        property.country && `Country: ${property.country}`,
        property.googleMap && "Google Maps location available",
      ].filter(Boolean),
      "Additional Information": [
        // New tab for Flutter form specific fields
        property.adminName && `Note: ${property.adminName}`,
        property.listingInstructions &&
          `Special Instructions: ${property.listingInstructions}`,
        property.priceNegotiable && "Price is negotiable",
        property.marketingPreference &&
          `Marketing Preference: ${
            property.marketingPreference === "listAndPromote"
              ? "List and Promote"
              : "List Only"
          }`,
        property.listingType &&
          `Listing Type: ${
            property.listingType === "for_rent" ? "For Rent" : "For Sale"
          }`,
        property.quantity > 1 && `Quantity Available: ${property.quantity}`,
      ].filter(Boolean),
    };
  };

  if (loading) {
    return (
      <div
        style={{
          background: "#f5f2ef",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <Spin size="large" />
        <Text style={{ fontSize: "16px", color: "#666", fontStyle: "italic" }}>
          Loading exclusive property details...
        </Text>
      </div>
    );
  }

  if (!property) {
    return (
      <div
        style={{
          background: "#f5f2ef",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <FaHome style={{ fontSize: 48, color: "#999" }} />
        <Text style={{ fontSize: "18px", color: "#666" }}>
          Property listing not found
        </Text>
        <Text
          style={{
            fontSize: "14px",
            color: "#999",
            textAlign: "center",
            maxWidth: 300,
          }}
        >
          This property may have been recently sold or is no longer available.
        </Text>
        <Button
          type="primary"
          onClick={() => window.history.back()}
          style={{
            marginTop: 16,
            background: "#011F3A",
            borderColor: "#011F3A",
          }}
        >
          Browse Available Properties
        </Button>
      </div>
    );
  }

  const tabContent = getTabContent();
  const primaryImage = getPrimaryImage();
  const thumbnailImages = getThumbnailImages();
  const displayTitle = getDisplayTitle();

  return (
    <div style={{ background: "#f5f2ef", minHeight: "100vh" }}>
      <CommonHeader bgColor="#DDC7BB" />

      {/* ---------- MAIN SECTION ---------- */}
      <section
        style={{
          maxWidth: 1200,
          margin: "60px auto",
          display: "flex",
          gap: 40,
          justifyContent: "center",
          flexWrap: "wrap",
          padding: "0 20px",
        }}
      >
        {/* LEFT IMAGE */}
        <div
          style={{
            flex: "1 1 520px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {primaryImage ? (
            <img
              src={primaryImage}
              alt={displayTitle}
              style={{
                width: "100%",
                height: 520,
                borderRadius: 16,
                objectFit: "cover",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : null}
          <div
            style={{
              width: "100%",
              height: 520,
              borderRadius: 16,
              background: "linear-gradient(135deg, #f5f2ef 0%, #e8e4df 100%)",
              display: primaryImage ? "none" : "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 12,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              border: "2px dashed #DDC7BB",
            }}
          >
            <FaImage style={{ fontSize: 48, color: "#B8A99A" }} />
            <Text
              style={{ color: "#8A7B6B", fontSize: 16, fontStyle: "italic" }}
            >
              Professional photography available upon request
            </Text>
          </div>

          {/* Thumbnail Images */}
          {thumbnailImages.length > 0 ? (
            <div style={{ display: "flex", gap: 12, marginTop: 14 }}>
              {thumbnailImages.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt={`Property view ${i + 1}`}
                  style={{
                    width: 110,
                    height: 90,
                    borderRadius: 10,
                    objectFit: "cover",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                    cursor: "pointer",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              ))}
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                gap: 12,
                marginTop: 14,
                justifyContent: "center",
              }}
            >
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  style={{
                    width: 110,
                    height: 90,
                    borderRadius: 10,
                    background:
                      "linear-gradient(135deg, #f5f2ef 0%, #e8e4df 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                    border: "1px dashed #DDC7BB",
                  }}
                >
                  <Text
                    style={{
                      color: "#B8A99A",
                      fontSize: 10,
                      textAlign: "center",
                    }}
                  >
                    View {i}
                  </Text>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT CARD */}
        <div
          style={{
            flex: "1 1 520px",
            minHeight: 520,
            background: "#fff",
            padding: "40px",
            borderRadius: 16,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "flex-start",
            textAlign: "left",
          }}
        >
          <div style={{ width: "100%" }}>
            {/* Property Type and Ownership Status Row */}
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 12 }}
            >
              <Col>
                <Text
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#2196F3",
                    textTransform: "uppercase",
                  }}
                >
                  {property.propertyType || "Property"} •{" "}
                  {property.ownershipStatus || "Freehold"}
                </Text>
              </Col>
              {property.exclusivity && (
                <Col>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <Text
                      style={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#011F3A",
                        textTransform: "uppercase",
                      }}
                    >
                      PREMIUM
                    </Text>
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        backgroundColor: "#16A34A",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Text
                        style={{
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: "10px",
                        }}
                      >
                        N
                      </Text>
                    </div>
                  </div>
                </Col>
              )}
            </Row>

            {/* Main Title */}
            <Title
              level={3}
              style={{
                marginTop: 0,
                marginBottom: 12,
                color: "#001254",
                lineHeight: 1.3,
                fontWeight: 600,
              }}
            >
              {displayTitle}
            </Title>

            {/* Admin Name (from Flutter form) */}
            {property.adminName && (
              <Text
                style={{
                  display: "block",
                  marginBottom: 12,
                  color: "#16A34A",
                  fontSize: 14,
                  fontWeight: 600,
                  fontStyle: "italic",
                }}
              >
                {property.adminName}
              </Text>
            )}

            {/* Location */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <FaMapMarkerAlt
                style={{ color: "#001254", marginRight: 6, fontSize: 16 }}
              />
              <Text style={{ color: "#001254", fontSize: 15, fontWeight: 500 }}>
                {property.city || "Location not specified"}
                {property.landmark && ` • ${property.landmark}`}
              </Text>
            </div>

            {/* Price Section */}
            <Text
              style={{
                display: "block",
                marginBottom: 4,
                color: "#5C5C5C",
                fontSize: 15,
                fontStyle: "italic",
              }}
            >
              {property.finalPrice > 0 || property.listPrice > 0
                ? "Investment starting from"
                : "Premium pricing available"}
            </Text>
            <Title
              level={2}
              style={{
                marginTop: 0,
                marginBottom: 12,
                color: "#001254",
                fontWeight: 700,
                fontSize: 30,
              }}
            >
              {property.getDisplayPrice() || "Price upon consultation"}
            </Title>

            {/* Property Features */}
            <div
              style={{
                display: "flex",
                gap: 16,
                flexWrap: "wrap",
                marginBottom: 16,
              }}
            >
              {property.bedrooms > 0 && (
                <Text
                  style={{
                    background: "#EAF5FFF2",
                    borderRadius: 8,
                    padding: "4px 12px",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#001254",
                  }}
                >
                  {property.bedrooms} Bed
                </Text>
              )}
              {property.bathrooms > 0 && (
                <Text
                  style={{
                    background: "#EAF5FFF2",
                    borderRadius: 8,
                    padding: "4px 12px",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#001254",
                  }}
                >
                  {property.bathrooms} Bath
                </Text>
              )}
              {property.parkingSpaces > 0 && (
                <Text
                  style={{
                    background: "#EAF5FFF2",
                    borderRadius: 8,
                    padding: "4px 12px",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#001254",
                  }}
                >
                  {property.parkingSpaces} Parking
                </Text>
              )}
              {property.getPropertySize() && (
                <Text
                  style={{
                    background: "#EAF5FFF2",
                    borderRadius: 8,
                    padding: "4px 12px",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#001254",
                  }}
                >
                  {property.getPropertySize()}
                </Text>
              )}
            </div>

            {/* Property Status */}
            <Button
              style={{
                background: "#EAF5FFF2",
                borderRadius: 8,
                border: "none",
                height: 36,
                fontSize: 14,
                fontWeight: 500,
                color: "#001254",
                padding: "0 16px",
                fontStyle: "italic",
              }}
            >
              {property.propertyStatus || "Available for viewing"}
              {property.priceNegotiable && " • Price Negotiable"}
            </Button>
          </div>

          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              marginTop: 20,
            }}
          >
            <Button
              type="primary"
              size="large"
              style={{
                background: "#011F3A",
                borderColor: "#011F3A",
                borderRadius: 8,
                height: 44,
                fontSize: 15,
                fontWeight: 500,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "70%",
                boxShadow: "none",
                opacity: 0.6,
                cursor: "not-allowed",
              }}
              onClick={handleWhatsAppClick}
              disabled
            >
              <FaWhatsapp
                style={{ marginRight: 10, fontSize: 20, color: "#25D366" }}
              />{" "}
              Contact Agent (Coming Soon)
            </Button>
          </div>
        </div>
      </section>

      {/* ---------- DETAILS SECTION ---------- */}
      <section
        style={{ maxWidth: 1200, margin: "40px auto", padding: "0 20px" }}
      >
        <Card
          style={{
            borderRadius: 16,
            padding: 30,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            background: "#fff",
          }}
        >
          {/* Tab Buttons */}
          <div
            style={{
              display: "flex",
              gap: 40,
              flexWrap: "wrap",
              fontWeight: 500,
              color: "#444",
              marginBottom: 20,
            }}
          >
            {[
              "Overview",
              "Specifications",
              "Location",
              "Additional Information",
            ].map((tab) => (
              <Text
                key={tab}
                onClick={() => setSelectedTab(tab)}
                style={{
                  cursor: "pointer",
                  position: "relative",
                  color: selectedTab === tab ? "#001254" : "#444",
                  borderBottom:
                    selectedTab === tab
                      ? "2px solid #001254"
                      : "2px solid transparent",
                  paddingBottom: 6,
                  transition: "all 0.3s ease",
                  fontSize: "15px",
                }}
              >
                {tab}
              </Text>
            ))}
          </div>

          {/* Tab Content */}
          <div style={{ borderTop: "1px solid #eee", paddingTop: 30 }}>
            {selectedTab === "Overview" && (
              <div>
                {tabContent.Overview.map((p, i) => (
                  <Paragraph
                    key={i}
                    style={{
                      fontSize: 15,
                      color: "#555",
                      lineHeight: 1.8,
                      marginBottom: 20,
                      fontStyle: i === 1 ? "italic" : "normal",
                    }}
                  >
                    {p}
                  </Paragraph>
                ))}
              </div>
            )}

            {selectedTab === "Specifications" && (
              <div style={{ fontSize: 15, color: "#555", lineHeight: 2 }}>
                {tabContent.Specifications.length > 0 ? (
                  tabContent.Specifications.map((item, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: 8,
                      }}
                    >
                      <span style={{ marginRight: 8 }}>•</span>
                      {item}
                    </div>
                  ))
                ) : (
                  <Text
                    style={{
                      color: "#8A7B6B",
                      fontStyle: "italic",
                      fontSize: 14,
                    }}
                  >
                    Complete specifications available during private
                    consultation
                  </Text>
                )}
              </div>
            )}

            {selectedTab === "Location" && (
              <div
                style={{
                  display: "flex",
                  gap: 20,
                  alignItems: "flex-start",
                  maxWidth: 600,
                }}
              >
                <div
                  style={{
                    width: 160,
                    height: 180,
                    borderRadius: 12,
                    background:
                      "linear-gradient(135deg, #f5f2ef 0%, #e8e4df 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 8,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    border: "1px dashed #DDC7BB",
                  }}
                >
                  <FaMapMarkerAlt style={{ color: "#B8A99A", fontSize: 24 }} />
                  <Text
                    style={{
                      color: "#8A7B6B",
                      textAlign: "center",
                      fontSize: 12,
                      fontStyle: "italic",
                    }}
                  >
                    Location Map
                    {!property.googleMap && "\nAvailable upon visit"}
                  </Text>
                </div>
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    fontSize: 14,
                    color: "#555",
                    lineHeight: 1.6,
                  }}
                >
                  {tabContent.Location.map((line, i) => (
                    <div key={i} style={{ marginBottom: 8 }}>
                      {line}
                    </div>
                  ))}
                  {tabContent.Location.length === 0 && (
                    <Text style={{ color: "#8A7B6B", fontStyle: "italic" }}>
                      Prime location details shared during viewing appointment
                    </Text>
                  )}
                  <Button
                    style={{
                      background: "#0053A0",
                      color: "#fff",
                      borderRadius: 8,
                      padding: "8px 16px",
                      fontWeight: 600,
                      marginTop: 15,
                      width: "fit-content",
                    }}
                    onClick={handleGetDirections}
                    disabled={!property.getFullAddress() && !property.googleMap}
                  >
                    Arrange Site Visit
                  </Button>
                </div>
              </div>
            )}

            {selectedTab === "Additional Information" && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                  paddingTop: 10,
                }}
              >
                <Text
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: "#001254",
                    textAlign: "left",
                  }}
                >
                  Additional Details
                </Text>
                {tabContent["Additional Information"].length > 0 ? (
                  tabContent["Additional Information"].map((info, i) => (
                    <Text
                      key={i}
                      style={{
                        fontSize: 15,
                        color: "#555",
                        lineHeight: 1.6,
                        textAlign: "left",
                        fontStyle: info.includes("Note:") ? "italic" : "normal",
                        color: info.includes("Note:") ? "#16A34A" : "#555",
                      }}
                    >
                      {info}
                    </Text>
                  ))
                ) : (
                  <Text
                    style={{
                      color: "#8A7B6B",
                      fontStyle: "italic",
                      fontSize: 14,
                    }}
                  >
                    Additional information available upon request
                  </Text>
                )}
              </div>
            )}
          </div>
        </Card>
      </section>

      {/* ---------- SIMILAR PROPERTIES ---------- */}
      {similarProperties.length > 0 && (
        <section
          style={{
            maxWidth: 1200,
            margin: "60px auto",
            borderRadius: 12,
            padding: "0 20px",
          }}
        >
          <Title
            level={4}
            style={{
              color: "#001254",
              marginBottom: 25,
              fontWeight: 700,
              fontSize: 35,
              textAlign: "center",
            }}
          >
            Similar Premium Properties
          </Title>
          <Row
            gutter={[20, 20]}
            style={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            {similarProperties.map((property) => (
              <Col
                key={property.id}
                xs={24}
                sm={12}
                md={8}
                lg={8}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: 20,
                }}
              >
                <PropertyCard property={property} />
              </Col>
            ))}
          </Row>
        </section>
      )}

      <CommonFooter />
    </div>
  );
};

export default PropertyDetail;
